import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { dashboardAPI, alertAPI, userAPI, getWebSocketURL } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, formatCurrency, formatRelativeTime, getRiskClass } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DraggableWidget, WidgetSettingsPanel } from '../components/DraggableWidget';
import { toast } from 'sonner';
import {
  Users,
  ArrowLeftRight,
  AlertTriangle,
  FileText,
  TrendingUp,
  TrendingDown,
  Shield,
  Activity,
  Eye,
  ChevronRight,
  RefreshCw,
  Wifi,
  WifiOff,
  Zap,
  Settings2,
  GripVertical,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const RISK_COLORS = {
  critical: '#FF003D',
  high: '#FF6B00',
  medium: '#FF9900',
  low: '#00FF94',
};

// Default widget configuration
const DEFAULT_WIDGETS = [
  { id: 'stats', title: 'Key Statistics', visible: true, order: 0 },
  { id: 'transactions', title: 'Transaction Activity', visible: true, order: 1 },
  { id: 'risk', title: 'Risk Distribution', visible: true, order: 2 },
  { id: 'alerts', title: 'Recent Alerts', visible: true, order: 3 },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [transactionTrends, setTransactionTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        // Save preferences
        saveWidgetPreferences(newOrder);
        return newOrder;
      });
    }
  };

  // Toggle widget visibility
  const toggleWidget = (widgetId) => {
    setWidgets((items) => {
      const newWidgets = items.map((item) =>
        item.id === widgetId ? { ...item, visible: !item.visible } : item
      );
      saveWidgetPreferences(newWidgets);
      return newWidgets;
    });
  };

  // Save widget preferences
  const saveWidgetPreferences = async (widgetConfig) => {
    try {
      await userAPI.updatePreferences({ dashboard_widgets: widgetConfig });
    } catch (error) {
      console.error('Failed to save widget preferences:', error);
    }
  };

  // Load widget preferences
  const loadWidgetPreferences = async () => {
    try {
      const prefs = await userAPI.getPreferences();
      if (prefs.data?.dashboard_widgets) {
        setWidgets(prefs.data.dashboard_widgets);
      }
    } catch (error) {
      console.error('Failed to load widget preferences:', error);
    }
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!user?.id || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = getWebSocketURL(user.id);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('Dashboard WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setWsConnected(false);
        console.log('Dashboard WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [user?.id]);

  const handleWebSocketMessage = (data) => {
    if (data.type === 'dashboard_update' && data.data) {
      // Update stats with new data
      setStats(prevStats => ({
        ...prevStats,
        ...data.data,
      }));
      
      // Add to live updates feed
      setLiveUpdates(prev => [{
        id: Date.now(),
        type: 'stats_update',
        message: 'Dashboard stats updated',
        timestamp: new Date().toISOString(),
      }, ...prev].slice(0, 5));
    } else if (data.type === 'new_alert' && data.data) {
      // Add new alert to the list
      setAlerts(prev => [data.data, ...prev].slice(0, 5));
      
      // Show toast notification
      toast.warning('New Alert', {
        description: data.data.title,
      });
      
      // Add to live updates feed
      setLiveUpdates(prev => [{
        id: Date.now(),
        type: 'new_alert',
        message: `New alert: ${data.data.title}`,
        timestamp: new Date().toISOString(),
      }, ...prev].slice(0, 5));
    }
  };

  useEffect(() => {
    fetchDashboardData();
    loadWidgetPreferences();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, alertsRes, riskRes, trendsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentAlerts(5),
        dashboardAPI.getRiskDistribution(),
        dashboardAPI.getTransactionTrends(14),
      ]);

      setStats(statsRes.data);
      setAlerts(alertsRes.data);

      // Transform risk distribution for pie chart
      const riskData = Object.entries(riskRes.data).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: value,
        color: RISK_COLORS[key] || '#64748B',
      }));
      setRiskDistribution(riskData);

      // Transform transaction trends
      setTransactionTrends(
        trendsRes.data.map((item) => ({
          date: item._id,
          transactions: item.count,
          flagged: item.flagged,
          amount: item.total_amount,
        }))
      );
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Entities',
      value: stats?.total_entities || 0,
      icon: Users,
      change: '+12%',
      positive: true,
    },
    {
      title: 'Transactions',
      value: stats?.total_transactions || 0,
      icon: ArrowLeftRight,
      change: '+8%',
      positive: true,
    },
    {
      title: 'Active Alerts',
      value: stats?.active_alerts || 0,
      icon: AlertTriangle,
      change: '-5%',
      positive: false,
      highlight: true,
    },
    {
      title: 'Open Cases',
      value: stats?.open_cases || 0,
      icon: FileText,
      change: '+3%',
      positive: true,
    },
    {
      title: 'High Risk Entities',
      value: stats?.high_risk_entities || 0,
      icon: Shield,
      change: '+15%',
      positive: false,
      danger: true,
    },
    {
      title: 'Flagged Transactions',
      value: stats?.flagged_transactions || 0,
      icon: Activity,
      change: '+7%',
      positive: false,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-48 bg-card rounded skeleton" />
            <div className="h-4 w-64 bg-card rounded skeleton mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 bg-card rounded-sm skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Intelligence Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            AEGIS AML/CTF Intelligence Overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Customize Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowWidgetSettings(true)}
            className="gap-2"
            data-testid="customize-btn"
          >
            <Settings2 className="w-4 h-4" />
            <span className="hidden sm:inline">Customize</span>
          </Button>
          {/* Live Updates Indicator */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            wsConnected 
              ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
              : 'bg-muted/50 text-muted-foreground border border-border'
          }`}>
            {wsConnected ? (
              <>
                <Wifi className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Live</span>
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              </>
            ) : (
              <>
                <WifiOff className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Offline</span>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            className="gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Live Updates Feed */}
      {liveUpdates.length > 0 && (
        <Card className="glass border-primary/30 bg-primary/5">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <Zap className="w-4 h-4 text-primary animate-pulse" />
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-primary">Live Updates</span>
                  <span className="text-[10px] text-muted-foreground">
                    {formatRelativeTime(liveUpdates[0]?.timestamp)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {liveUpdates[0]?.message}
                </p>
              </div>
              <Badge variant="outline" className="text-[10px]">
                {liveUpdates.length} update{liveUpdates.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className={`glass card-hover ${stat.highlight ? 'border-primary/50' : ''} ${
              stat.danger ? 'border-destructive/50' : ''
            }`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div
                  className={`w-10 h-10 rounded flex items-center justify-center ${
                    stat.danger
                      ? 'bg-destructive/20'
                      : stat.highlight
                      ? 'bg-primary/20'
                      : 'bg-secondary'
                  }`}
                >
                  <stat.icon
                    className={`w-5 h-5 ${
                      stat.danger
                        ? 'text-destructive'
                        : stat.highlight
                        ? 'text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                </div>
                <div
                  className={`flex items-center gap-1 text-xs font-mono ${
                    stat.positive ? 'text-[#00FF94]' : 'text-[#FF003D]'
                  }`}
                >
                  {stat.positive ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {stat.change}
                </div>
              </div>
              <div className="mt-3">
                <p className="kpi-value text-3xl text-white">
                  {formatNumber(stat.value)}
                </p>
                <p className="label-mono mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Transaction Trends */}
        <Card className="glass lg:col-span-2">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
              Transaction Activity (14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={transactionTrends}>
                  <defs>
                    <linearGradient id="colorTransactions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00F0FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorFlagged" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF003D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#FF003D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748B"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#64748B"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(222, 47%, 11%)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="transactions"
                    stroke="#00F0FF"
                    fillOpacity={1}
                    fill="url(#colorTransactions)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="flagged"
                    stroke="#FF003D"
                    fillOpacity={1}
                    fill="url(#colorFlagged)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Risk Distribution */}
        <Card className="glass">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
              Entity Risk Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64 flex items-center justify-center">
              {riskDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(222, 47%, 11%)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm">No data available</p>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {riskDistribution.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Alerts */}
      <Card className="glass">
        <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
          <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
            Recent Alerts
          </CardTitle>
          <Link to="/alerts">
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-white">
              View All
              <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-white/5">
            {alerts.length > 0 ? (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div
                    className={`alert-indicator h-12 ${alert.severity}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-white truncate">
                        {alert.title}
                      </h4>
                      <Badge
                        variant="outline"
                        className={`${getRiskClass(alert.severity)} text-[10px] font-mono uppercase`}
                      >
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {alert.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-muted-foreground">
                      {formatRelativeTime(alert.created_at)}
                    </p>
                    <Badge
                      variant="outline"
                      className="text-[10px] font-mono uppercase mt-1"
                    >
                      {alert.status}
                    </Badge>
                  </div>
                  <Link to={`/alerts/${alert.id}`}>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-white">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No recent alerts</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Widget Settings Panel */}
      {showWidgetSettings && (
        <WidgetSettingsPanel
          widgets={widgets}
          onToggle={toggleWidget}
          onClose={() => setShowWidgetSettings(false)}
        />
      )}
    </div>
  );
}
